import CustomSkeleton, {CARD_PRESET} from 'common/components/CustomSkeleton'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  FileText,
  GraduationCap,
  Info,
  Search,
  Trash2,
  Users,
} from 'lucide-react'
import React, {FC, useEffect, useId, useMemo, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'

import {TeacherFileCounts} from '../utils/teacherEntry'
import {
  BLOCKING_TEACHER_STATUSES,
  TeacherReviewStatus,
  similarTeachersOf,
  summarizeTeacherReview,
  TEACHER_FOUND_HINT_KEY,
  teacherDisplayName,
  teacherFoundBadge,
  teacherMatchesQuery,
} from '../utils/teacherReview'
import {ONBOARDING_QA_OCT18} from '../utils/onboardingQaOct18'
import TableBusyOverlay from './TableBusyOverlay'

/**
 * Review Teachers — the full-page review state of the Teachers tab, shown in the top stepper
 * as its own "Review Teachers" sub-step (URL /assign-subjects/review-teachers), never a popup.
 * Names and Employee IDs are edited inline; the only row action is
 * Delete (single or bulk). Nothing is auto-resolved: same-name teachers stay flagged until the
 * user changes a name or gives each a unique Employee ID, and Confirm stays disabled until
 * every blocking row is fixed.
 */

interface Props {
  isDark: boolean
  isHydrating?: boolean
  isSubmitting?: boolean
  /** `manualData.teachers` rows (loosely typed upstream). */
  teachers: any[]
  /**
   * Students and allocation lines found in the uploaded files (what the Add Teachers summary
   * used to show), as stat cards. Null when unknown (no file checked, or a reload): left out.
   */
  fileCounts?: TeacherFileCounts | null
  /** Replace the whole list (inline edits). */
  onTeachersChange: (next: any[]) => void
  /** Remove rows by original index; the parent owns the confirm dialog. */
  onDeleteRows: (indices: number[]) => void
  /** Saved teachers (by id) whose delete is on its way to the server: their icon spins. */
  deletingIds?: ReadonlySet<number>
  /**
   * Add Teachers' allocation lines confirmed with no classes, still waiting: "N need review",
   * with a button that reopens them in the "Which classes?" dialog (`onOpenPendingReview`).
   */
  pendingReviewCount?: number
  /** Their answers are on their way: the button spins. */
  pendingReviewBusy?: boolean
  onOpenPendingReview?: () => void
  onBack: () => void
  onContinue: () => void
}

const STATUS_KEYS: Record<TeacherReviewStatus, string> = {
  ok: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STATUS_OK',
  'name-required': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STATUS_NAME_REQUIRED',
  'same-name': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STATUS_SAME_NAME',
  'duplicate-id': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STATUS_DUPLICATE_ID',
  similar: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STATUS_SIMILAR',
  verify: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STATUS_VERIFY',
}

/** `reviewReasons` the server sends with "Please verify", in words; unknown codes are skipped. */
const REVIEW_REASON_KEYS: Record<string, string> = {
  LOW_OCR_CONFIDENCE: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.REASON_LOW_OCR_CONFIDENCE',
  LOW_CONFIDENCE: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.REASON_LOW_OCR_CONFIDENCE',
  EVIDENCE_NOT_FOUND: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.REASON_NOT_IN_SOURCE',
  VALUE_NOT_IN_SOURCE: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.REASON_NOT_IN_SOURCE',
  SIMILAR_NAME: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.REASON_SIMILAR_NAME',
}

const Step4TeacherReview: FC<Props> = ({
  isDark,
  isHydrating = false,
  isSubmitting = false,
  teachers,
  fileCounts = null,
  onTeachersChange,
  onDeleteRows,
  deletingIds,
  pendingReviewCount = 0,
  pendingReviewBusy = false,
  onOpenPendingReview,
  onBack,
  onContinue,
}) => {
  const intl = useIntl()
  const baseId = useId()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Set<number>>(() => new Set())

  // Row indices shift on delete, so any selection is stale the moment the list length changes.
  useEffect(() => {
    setSelected(new Set())
  }, [teachers.length])

  const summary = useMemo(() => summarizeTeacherReview(teachers), [teachers])

  const visible = useMemo(
    () =>
      teachers
        .map((row, index) => ({row, index}))
        .filter(({row}) => teacherMatchesQuery(row, query)),
    [teachers, query]
  )

  const allVisibleSelected = visible.length > 0 && visible.every(({index}) => selected.has(index))
  const someVisibleSelected = visible.some(({index}) => selected.has(index))

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) visible.forEach(({index}) => next.delete(index))
      else visible.forEach(({index}) => next.add(index))
      return next
    })
  }

  const toggleRow = (index: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const patchRow = (index: number, patch: Record<string, unknown>, drop: string[] = []) => {
    const next = [...teachers]
    const {...row} = next[index]
    drop.forEach((k) => delete row[k])
    next[index] = {...row, ...patch}
    onTeachersChange(next)
  }

  const commitName = (index: number, raw: string) => {
    const parts = raw.trim().split(/\s+/).filter(Boolean)
    // Editing the name is the user confirming it — the extraction "verify" marker goes too.
    patchRow(index, {first_name: parts[0] || '', last_name: parts.slice(1).join(' ') || ''}, [
      '_nameInput',
      '_needsReview',
    ])
  }

  /**
   * "Keep both": these are different people. The look-alike note goes from this row and, for the
   * teachers it named, from theirs too (they were flagged as a pair), so it is not asked again.
   */
  const keepBoth = (index: number) => {
    const others = new Set(similarTeachersOf(teachers, index).map((name) => name.toLowerCase()))
    const own = teacherDisplayName(teachers[index]).trim().toLowerCase()
    const next = teachers.map((row, i) => {
      if (i === index) {
        const {_similarTo, ...rest} = row
        return rest
      }
      if (!others.has(teacherDisplayName(row).trim().toLowerCase())) return row
      const left = (row._similarTo ?? []).filter(
        (name: string) => name.trim().toLowerCase() !== own
      )
      if (left.length === (row._similarTo ?? []).length) return row
      const {_similarTo, ...rest} = row
      return left.length > 0 ? {...rest, _similarTo: left} : rest
    })
    onTeachersChange(next)
  }

  const continueDisabled = !summary.canContinue || isSubmitting || isHydrating

  /** Why a row says "Please verify", from the server's `reviewReasons`; a plain hint otherwise. */
  const verifyTooltip = (row: any) => {
    const reasons: string[] = Array.isArray(row?._reviewReasons) ? row._reviewReasons : []
    const known = reasons
      .map((reason) => REVIEW_REASON_KEYS[reason])
      .filter((key): key is string => Boolean(key))
      .map((key) => intl.formatMessage({id: key}))
    return known.length > 0
      ? known.join(' ')
      : intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.VERIFY_TOOLTIP'})
  }

  const renderStatus = (status: TeacherReviewStatus, row?: any) => {
    const blocking = BLOCKING_TEACHER_STATUSES.has(status)
    // `similar` is advisory like `verify`: it never blocks Continue.
    const tone = status === 'ok' ? 'ok' : blocking ? 'attention' : 'verify'
    return (
      <span
        className={`step4-tr-status step4-tr-status--${tone}`}
        title={status === 'verify' ? verifyTooltip(row) : undefined}
      >
        {status !== 'ok' && <AlertTriangle size={12} aria-hidden />}
        {intl.formatMessage({id: STATUS_KEYS[status]})}
      </span>
    )
  }

  // The files' students and allocation lines (counted on Add Teachers) as cards beside the
  // teacher counts: Students only when the files had some, Allocation whenever they were checked.
  const showStudents = Boolean(fileCounts && fileCounts.students > 0)
  const statCount = 3 + (showStudents ? 1 : 0) + (fileCounts ? 1 : 0)

  /**
   * A card's longer explanation, behind an (i) in its corner: inline it made those cards taller
   * than the two teacher cards beside them.
   */
  const renderStatInfo = (name: string, text: string) => (
    <OverlayTrigger
      placement='bottom'
      trigger={['hover', 'focus']}
      // Fixed, not absolute: the tooltip is portalled to <body>, and an absolute one painted
      // before Popper places it sits under the full-height wizard, so the page grows a scrollbar
      // for a frame and the whole screen jumps (and the jump can drop the hover, again and again).
      popperConfig={{strategy: 'fixed'}}
      overlay={
        <Tooltip id={`${baseId}-${name}-info`} className='ta-tooltip step4-tr-stat-tooltip'>
          {text}
        </Tooltip>
      }
    >
      <button type='button' className='step4-tr-stat-info' aria-label={text}>
        <Info size={16} aria-hidden />
      </button>
    </OverlayTrigger>
  )

  /** Several saved teachers are being deleted together: one spinner for the whole table. */
  const bulkDeleting = (deletingIds?.size ?? 0) > 1

  return (
    <div className={`step4-teacher-review${isDark ? ' step4-teacher-review--dark' : ''}`}>
      <div className='step4-tr-header'>
        <h2 className='step4-tr-title'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.HEADING'})}
        </h2>
        <p className='step4-tr-subtitle'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SUBHEADING'})}
        </p>
      </div>

      <div className={`step4-tr-stats step4-tr-stats--${statCount}`}>
        <div className='step4-tr-stat'>
          <span className='step4-tr-stat-icon step4-tr-stat-icon--teachers'>
            <Users size={22} aria-hidden />
          </span>
          <div className='step4-tr-stat-body'>
            <span className='step4-tr-stat-value'>{teachers.length}</span>
            <span className='step4-tr-stat-label'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_TEACHERS',
              })}
            </span>
          </div>
        </div>
        {/* Must fix: blocks Continue. To check: advisory rows ("Possibly a duplicate",
            "Please verify") — counted, so the header always matches what the rows say. */}
        <div
          className={`step4-tr-stat${summary.attentionCount > 0 ? ' step4-tr-stat--attention' : ''}`}
        >
          <span className='step4-tr-stat-icon step4-tr-stat-icon--attention'>
            <AlertTriangle size={22} aria-hidden />
          </span>
          <div className='step4-tr-stat-body'>
            <span className='step4-tr-stat-value'>{summary.attentionCount}</span>
            <span className='step4-tr-stat-label'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_ATTENTION',
              })}
            </span>
          </div>
        </div>
        <div className={`step4-tr-stat${summary.checkCount > 0 ? ' step4-tr-stat--check' : ''}`}>
          <span className='step4-tr-stat-icon step4-tr-stat-icon--check'>
            <Info size={22} aria-hidden />
          </span>
          <div className='step4-tr-stat-body'>
            <span className='step4-tr-stat-value'>{summary.checkCount}</span>
            <span className='step4-tr-stat-label'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_TO_CHECK',
              })}
            </span>
          </div>
        </div>
        {fileCounts && showStudents && (
          <div className='step4-tr-stat'>
            <span className='step4-tr-stat-icon step4-tr-stat-icon--students'>
              <GraduationCap size={22} aria-hidden />
            </span>
            <div className='step4-tr-stat-body'>
              <span className='step4-tr-stat-value'>{fileCounts.students}</span>
              <span className='step4-tr-stat-label'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_STUDENTS',
                })}
              </span>
            </div>
            {renderStatInfo(
              'students',
              intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_STUDENTS_DESC'},
                {count: fileCounts.students}
              )
            )}
          </div>
        )}
        {fileCounts && (
          <div className='step4-tr-stat'>
            <span className='step4-tr-stat-icon step4-tr-stat-icon--allocation'>
              <FileText size={22} aria-hidden />
            </span>
            <div className='step4-tr-stat-body'>
              <span className='step4-tr-stat-value'>{fileCounts.allocation}</span>
              <span className='step4-tr-stat-label'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_ALLOCATION',
                })}
              </span>
              {/* A re-upload: of these, the cells that already have their teacher and the new. */}
              {(fileCounts.alreadyAssigned ?? 0) > 0 && (
                <span className='step4-tr-stat-label'>
                  {intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ASSIGNMENT_SPLIT_SHORT'},
                    {
                      already: fileCounts.alreadyAssigned ?? 0,
                      fresh: fileCounts.newAssignments ?? 0,
                    }
                  )}
                </span>
              )}
            </div>
            {renderStatInfo(
              'allocation',
              fileCounts.allocation > 0
                ? intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_ALLOCATION_DESC'},
                    {count: fileCounts.allocation}
                  )
                : intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.STAT_ALLOCATION_NONE_DESC',
                  })
            )}
          </div>
        )}
      </div>

      {/* The files' assignments: confirmed cells, proposals still waiting for "Which classes?"
          and lines with no proposal. Proposals are not in the grid (nor its progress) yet. */}
      {ONBOARDING_QA_OCT18 &&
        fileCounts &&
        (fileCounts.allocation > 0 ||
          (fileCounts.awaitingReview ?? 0) > 0 ||
          (fileCounts.unresolvedLines ?? 0) > 0) && (
          <p className='step4-tr-info' role='status'>
            <FileText size={16} aria-hidden />
            {[
              intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.ASSIGNMENTS_CONFIRMED'},
                {count: fileCounts.allocation}
              ),
              (fileCounts.awaitingReview ?? 0) > 0 &&
                intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.ASSIGNMENTS_AWAITING'},
                  {count: fileCounts.awaitingReview}
                ),
              (fileCounts.unresolvedLines ?? 0) > 0 &&
                intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.LINES_UNRESOLVED'},
                  {count: fileCounts.unresolvedLines}
                ),
            ]
              .filter(Boolean)
              .join(' ')}
          </p>
        )}

      {/* Lines confirmed on Add Teachers with no classes: still waiting, never hidden. */}
      {pendingReviewCount > 0 && onOpenPendingReview && (
        <div className='step4-tr-info d-flex flex-wrap align-items-center gap-3' role='status'>
          <AlertTriangle size={16} aria-hidden />
          <span className='flex-grow-1'>
            {intl.formatMessage(
              {id: 'DATA_UPLOAD.QUESTIONS.LINES_NEED_REVIEW'},
              {count: pendingReviewCount}
            )}
          </span>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            disabled={pendingReviewBusy}
            onClick={onOpenPendingReview}
          >
            {pendingReviewBusy && (
              <span className='spinner-border spinner-border-sm me-2' aria-hidden />
            )}
            {intl.formatMessage(
              {id: 'DATA_UPLOAD.QUESTIONS.REVIEW_COUNT'},
              {count: pendingReviewCount}
            )}
          </button>
        </div>
      )}

      <p className='step4-tr-info'>
        <Info size={16} aria-hidden />
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SAME_NAME_INFO'})}
      </p>

      <div className='step4-tr-card'>
        <div className='step4-tr-toolbar'>
          <label className='step4-tr-check'>
            <input
              type='checkbox'
              checked={allVisibleSelected}
              ref={(el) => {
                if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected
              }}
              onChange={toggleAll}
              disabled={visible.length === 0}
              aria-label={intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SELECT_ALL',
              })}
            />
          </label>
          <button
            type='button'
            className='step4-tr-bulk-delete'
            disabled={selected.size === 0}
            onClick={() => onDeleteRows(Array.from(selected).sort((a, b) => a - b))}
          >
            <Trash2 size={14} aria-hidden />
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.DELETE_SELECTED'},
              {count: selected.size}
            )}
          </button>
          <div className='step4-tr-toolbar-right'>
            <label className='step4-tr-search'>
              <Search size={15} aria-hidden />
              <input
                type='search'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SEARCH_PLACEHOLDER',
                })}
                aria-label={intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SEARCH_PLACEHOLDER',
                })}
              />
            </label>
            <span className='step4-tr-count'>
              {intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.COUNT'},
                {count: teachers.length}
              )}
            </span>
          </div>
        </div>

        {isHydrating ? (
          <div className='p-4'>
            <CustomSkeleton variant={CARD_PRESET.TABLE} />
          </div>
        ) : (
          <div style={{position: 'relative'}}>
            {/* Several rows deleting at once: one spinner over the table, not one per row. */}
            {bulkDeleting && (
              <TableBusyOverlay
                isDark={isDark}
                label={intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.DELETING'},
                  {count: deletingIds?.size ?? 0}
                )}
              />
            )}
            <div className='step4-tr-table-scroll'>
              <table className='step4-tr-table'>
                <thead>
                  <tr>
                    <th className='step4-tr-col-check' />
                    <th className='step4-tr-col-num'>#</th>
                    <th>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.COL_TEACHER_NAME',
                      })}
                    </th>
                    <th className='step4-tr-col-id'>
                      <span className='step4-tr-th-with-hint'>
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.EMPLOYEE_ID'})}
                        <span
                          className='step4-tr-th-hint'
                          title={intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.EMPLOYEE_ID_TOOLTIP',
                          })}
                        >
                          <Info size={13} aria-hidden />
                        </span>
                      </span>
                    </th>
                    <th className='step4-tr-col-status'>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.COL_STATUS',
                      })}
                    </th>
                    <th className='step4-tr-col-actions' />
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={6} className='step4-tr-empty'>
                        {intl.formatMessage({
                          id:
                            teachers.length === 0
                              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.NO_TEACHERS'
                              : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.NO_MATCH',
                        })}
                      </td>
                    </tr>
                  )}
                  {visible.map(({row, index}) => {
                    const status = summary.statuses[index]
                    const blocking = BLOCKING_TEACHER_STATUSES.has(status)
                    const rowClass = [
                      'step4-tr-row',
                      blocking ? 'step4-tr-row--attention' : '',
                      status === 'verify' || status === 'similar' ? 'step4-tr-row--verify' : '',
                      selected.has(index) ? 'step4-tr-row--selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')
                    const nameId = `${baseId}-name-${index}`
                    return (
                      <tr key={index} className={rowClass}>
                        <td className='step4-tr-col-check'>
                          <label className='step4-tr-check'>
                            <input
                              type='checkbox'
                              checked={selected.has(index)}
                              onChange={() => toggleRow(index)}
                              aria-label={intl.formatMessage(
                                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SELECT_ROW'},
                                {index: index + 1}
                              )}
                            />
                          </label>
                        </td>
                        <td className='step4-tr-col-num'>{index + 1}</td>
                        <td>
                          <input
                            id={nameId}
                            type='text'
                            className={`step4-tr-input${status === 'name-required' ? ' step4-tr-input--error' : ''}`}
                            value={teacherDisplayName(row)}
                            placeholder={intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.FULL_NAME_PLACEHOLDER',
                            })}
                            title={
                              row._sourceFile
                                ? intl.formatMessage(
                                    {
                                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SOURCE_FILE',
                                    },
                                    {file: row._sourceFile}
                                  )
                                : undefined
                            }
                            onChange={(e) => patchRow(index, {_nameInput: e.target.value})}
                            onBlur={(e) => commitName(index, e.target.value)}
                          />
                          {/* A staff list followed by its allocation lines names the same teacher
                            once per class. Those lines were folded into this one person, so the
                            badge lets the user check the fold rather than wonder where they went. */}
                          {ONBOARDING_QA_OCT18
                            ? (() => {
                                // "Found in 2 files" / "Found twice": repeated entries of the
                                // upload that were combined into this one teacher.
                                const found = teacherFoundBadge(row)
                                return (
                                  found && (
                                    <span
                                      className='step4-tr-merged-lines'
                                      title={intl.formatMessage({id: TEACHER_FOUND_HINT_KEY})}
                                    >
                                      {intl.formatMessage({id: found.id}, found.values)}
                                    </span>
                                  )
                                )
                              })()
                            : Number(row._mergedLines) > 1 && (
                            <span
                              className='step4-tr-merged-lines'
                              title={intl.formatMessage(
                                {
                                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.MERGED_LINES_HINT',
                                },
                                {count: row._mergedLines}
                              )}
                            >
                              {intl.formatMessage(
                                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.MERGED_LINES'},
                                {count: row._mergedLines}
                              )}
                            </span>
                          )}
                          {/* The server thinks this may be the same person as another teacher
                            (a spelling variant, one extra word). Both stay until the user says. */}
                          {status === 'similar' && (
                            <span className='step4-tr-similar' role='note'>
                              <span className='step4-tr-similar-text'>
                                {intl.formatMessage(
                                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SIMILAR_TO'},
                                  {names: similarTeachersOf(teachers, index).join(', ')}
                                )}
                              </span>
                              <span className='step4-tr-similar-actions'>
                                <button
                                  type='button'
                                  className='step4-tr-similar-btn'
                                  onClick={() => keepBoth(index)}
                                >
                                  {intl.formatMessage({
                                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SIMILAR_KEEP_BOTH',
                                  })}
                                </button>
                                <button
                                  type='button'
                                  className='step4-tr-similar-btn step4-tr-similar-btn--remove'
                                  onClick={() => onDeleteRows([index])}
                                >
                                  {intl.formatMessage({
                                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.SIMILAR_REMOVE',
                                  })}
                                </button>
                              </span>
                            </span>
                          )}
                        </td>
                        <td className='step4-tr-col-id'>
                          <input
                            type='text'
                            className={`step4-tr-input${status === 'same-name' || status === 'duplicate-id' ? ' step4-tr-input--warn' : ''}`}
                            value={row.employeeId ?? ''}
                            // Optional everywhere except a same-name group, where each member
                            // must carry a unique ID — the placeholder says so instead of "Optional".
                            required={status === 'same-name'}
                            aria-invalid={status === 'same-name' || status === 'duplicate-id'}
                            placeholder={intl.formatMessage({
                              id:
                                status === 'same-name'
                                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.EMPLOYEE_ID_REQUIRED'
                                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.EMPLOYEE_ID_PLACEHOLDER',
                            })}
                            onChange={(e) => patchRow(index, {employeeId: e.target.value})}
                          />
                        </td>
                        <td className='step4-tr-col-status'>{renderStatus(status, row)}</td>
                        <td className='step4-tr-col-actions'>
                          <button
                            type='button'
                            className='step4-tr-delete'
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onDeleteRows([index])}
                            disabled={Boolean(deletingIds?.has(Number((row as any)?.id)))}
                            aria-label={intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.DELETE_ROW',
                            })}
                            title={intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.DELETE_ROW',
                            })}
                          >
                            {!bulkDeleting && deletingIds?.has(Number((row as any)?.id)) ? (
                              <span
                                className='spinner-border spinner-border-sm'
                                role='status'
                                aria-hidden='true'
                              />
                            ) : (
                              <Trash2 size={16} aria-hidden />
                            )}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className='step4-tr-footer'>
        <button
          type='button'
          className='onboarding-btn-back'
          disabled={isSubmitting}
          onClick={onBack}
        >
          <ArrowLeft size={16} className='me-2' />
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
        </button>
        <div className='step4-tr-footer-cta'>
          <button
            type='button'
            className='onboarding-btn-next'
            disabled={continueDisabled}
            onClick={onContinue}
          >
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm align-middle me-2' />
                {intl.formatMessage({id: 'COMMON.LOADING'})}
              </>
            ) : (
              <>
                {/* Phones: the full label would wrap inside the half-width button. */}
                <span className='d-none d-sm-inline'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.CONFIRM_CONTINUE',
                  })}
                </span>
                <span className='d-sm-none'>{intl.formatMessage({id: 'GENERAL.CONTINUE'})}</span>
                <ArrowRight size={16} className='ms-2' />
              </>
            )}
          </button>
          {summary.attentionCount > 0 && (
            <span className='step4-tr-footer-hint'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.CONTINUE_HINT',
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default Step4TeacherReview
